import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { HiOutlineUserGroup } from 'react-icons/hi';
import { TbWallet } from 'react-icons/tb';
import { useWallet } from '../context/WalletContext';
import { useToast } from '../context/ToastContext';
import { api } from '../services/api';
import { shortAddress } from '../services/wallet';
import { TeamSkeleton } from '../components/Skeleton';
import PageHeader from '../components/PageHeader';

function MembersTable({ rows }) {
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Wallet</th>
            <th>Upline</th>
            <th>Invested</th>
            <th>Earned</th>
            <th>Joined</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.id}>
              <td className="mono" title={m.wallet_address}>
                {shortAddress(m.wallet_address)}
              </td>
              <td className="mono" title={m.upline_wallet || ''}>
                {m.upline_wallet ? shortAddress(m.upline_wallet) : '—'}
              </td>
              <td>{Number(m.total_invested || 0).toFixed(2)}</td>
              <td>{Number(m.total_earned || 0).toFixed(4)}</td>
              <td>{new Date(m.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
          {!rows.length ? (
            <tr>
              <td colSpan={5} className="muted">
                No members at this level.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

export default function TeamPage() {
  const { user, openConnect, connectOpening, loading } = useWallet();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [members, setMembers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [levels, setLevels] = useState([]);
  const [filterLevel, setFilterLevel] = useState('all');
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      setPageLoading(false);
      return;
    }
    let cancelled = false;
    setPageLoading(true);
    api
      .myTeam()
      .then((d) => {
        if (cancelled) return;
        setMembers(d.members || []);
        setSummary(d.summary || null);
        setLevels(d.levels || []);
      })
      .catch((e) => {
        if (!cancelled) toastRef.current.error(e.message || 'Failed to load team');
      })
      .finally(() => {
        if (!cancelled) setPageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.wallet_address, loading]);

  const filtered = useMemo(() => {
    if (filterLevel === 'all') return members;
    return members.filter((m) => Number(m.level) === Number(filterLevel));
  }, [members, filterLevel]);

  const levelsToShow = useMemo(() => {
    if (filterLevel === 'all') return levels;
    return levels.filter((l) => String(l.level) === String(filterLevel));
  }, [levels, filterLevel]);

  if (loading || (user && pageLoading)) {
    return <TeamSkeleton />;
  }

  if (!user) {
    return (
      <>
        <PageHeader title="Team" subtitle="Downline members by level" />
        <motion.div
          className="panel panel--glow"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="eyebrow">Team</div>
          <h2>Connect to view your downline</h2>
          <p className="muted">Your team tree is available after wallet login.</p>
          <div className="stack">
            <button
              className={`btn btn--primary${connectOpening ? ' is-busy' : ''}`}
              type="button"
              onClick={() => openConnect()}
              disabled={connectOpening}
              aria-busy={connectOpening}
            >
              {connectOpening ? <span className="btn__spin" aria-hidden /> : <TbWallet />}
              {connectOpening ? 'Connecting…' : 'Connect Wallet'}
            </button>
          </div>
        </motion.div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Team" subtitle="Downline members by level" />
      <motion.section
        className="panel panel--glow panel--tint-blue"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="eyebrow">
          <HiOutlineUserGroup style={{ marginRight: 4 }} />
          Team
        </div>
        <h2 style={{ marginBottom: '0.35rem' }}>Your downline</h2>
        <p className="muted" style={{ marginTop: 0, maxWidth: '36rem', lineHeight: 1.55 }}>
          Level 1 are members who registered with your invite link. Deeper levels joined under your
          team. Share your invite to grow.
        </p>
        <div className="wallet-balances">
          <div className="stat">
            <span>Total members</span>
            <strong>{summary?.total_members ?? 0}</strong>
          </div>
          <div className="stat">
            <span>Direct (L1)</span>
            <strong>{summary?.direct_count ?? 0}</strong>
          </div>
          <div className="stat">
            <span>Max level</span>
            <strong>{summary?.max_level ?? 0}</strong>
          </div>
          <div className="stat">
            <span>Team invested</span>
            <strong>
              {Number(summary?.total_invested || 0).toFixed(2)}
              <em>USDT</em>
            </strong>
          </div>
        </div>
      </motion.section>

      {levels.length ? (
        <div className="feature-grid" style={{ marginTop: '1rem' }}>
          {levels.map((lvl, i) => (
            <motion.button
              key={lvl.level}
              type="button"
              className={`feature team-level-card${
                String(filterLevel) === String(lvl.level) ? ' is-active' : ''
              }`}
              onClick={() =>
                setFilterLevel((prev) =>
                  String(prev) === String(lvl.level) ? 'all' : lvl.level
                )
              }
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.04 * i }}
            >
              <h3>Level {lvl.level}</h3>
              <p>
                <strong>{lvl.count}</strong> member{lvl.count === 1 ? '' : 's'} ·{' '}
                {Number(lvl.invested || 0).toFixed(2)} USDT invested
              </p>
            </motion.button>
          ))}
        </div>
      ) : null}

      <motion.section
        className="panel"
        style={{ marginTop: '1rem' }}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.08 }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '0.75rem',
            flexWrap: 'wrap',
            alignItems: 'center',
            marginBottom: '0.85rem',
          }}
        >
          <h3 style={{ margin: 0 }}>
            {filterLevel === 'all' ? 'All levels' : `Level ${filterLevel}`}
          </h3>
          <div className="stack" style={{ marginTop: 0 }}>
            <button
              type="button"
              className={`btn ${filterLevel === 'all' ? 'btn--primary' : 'btn--ghost'}`}
              style={{ padding: '0.45rem 0.75rem', fontSize: '0.78rem' }}
              onClick={() => setFilterLevel('all')}
            >
              All ({members.length})
            </button>
            {levels.map((lvl) => (
              <button
                key={lvl.level}
                type="button"
                className={`btn ${
                  String(filterLevel) === String(lvl.level) ? 'btn--primary' : 'btn--ghost'
                }`}
                style={{ padding: '0.45rem 0.75rem', fontSize: '0.78rem' }}
                onClick={() => setFilterLevel(lvl.level)}
              >
                L{lvl.level} ({lvl.count})
              </button>
            ))}
          </div>
        </div>

        {!members.length ? (
          <p className="muted" style={{ margin: 0 }}>
            No downline members yet — share your invite link to grow your team.
          </p>
        ) : filterLevel === 'all' ? (
          levelsToShow.map((lvl) => (
            <div key={lvl.level} className="team-level-block">
              <div className="team-level-block__head">
                <span className="badge">Level {lvl.level}</span>
                <span className="muted">
                  {lvl.count} member{lvl.count === 1 ? '' : 's'} ·{' '}
                  {Number(lvl.invested || 0).toFixed(2)} USDT
                </span>
              </div>
              <MembersTable rows={lvl.members || []} />
            </div>
          ))
        ) : (
          <MembersTable rows={filtered} />
        )}
      </motion.section>
    </>
  );
}
